// Mock data and calculations for the Retirement Planner

export const mockSuccessStories = [
  {
    name: "Rajesh Kumar",
    initials: "RK",
    age: 42,
    profession: "Software Engineer",
    testimonial: "Started planning at 35 with a ₹15,000 monthly SIP. Now I'm confident about retiring comfortably at 60 with a corpus of ₹2.5 crores. The systematic approach really works!",
    sipAmount: 15000,
    targetCorpus: "2.5",
    retirementGoal: "Retire at 60"
  },
  {
    name: "Priya Sharma",
    initials: "PS",
    age: 38,
    profession: "Doctor",
    testimonial: "As a single mother, financial security was my biggest worry. This retirement plan gave me clarity and confidence. My ₹25,000 SIP is building a strong future for me and my daughter.",
    sipAmount: 25000,
    targetCorpus: "4.2",
    retirementGoal: "Early retirement at 55"
  },
  {
    name: "Mohan Reddy",
    initials: "MR",
    age: 45,
    profession: "Business Owner",
    testimonial: "Despite having my own business, I realized the importance of systematic retirement planning. The inflation-adjusted calculations opened my eyes to the real corpus I needed.",
    sipAmount: 35000,
    targetCorpus: "3.8",
    retirementGoal: "Maintain lifestyle post-retirement"
  },
  {
    name: "Sunita Iyer",
    initials: "SI",
    age: 33,
    profession: "Teacher",
    testimonial: "Starting early made all the difference. With just ₹8,000 monthly SIP, I'm on track to build a retirement corpus of ₹1.8 crores by age 58. Compound interest is magical!",
    sipAmount: 8000,
    targetCorpus: "1.8",
    retirementGoal: "Comfortable retirement"
  },
  {
    name: "Vikram Rao",
    initials: "VR",
    age: 40,
    profession: "Bank Manager",
    testimonial: "The detailed breakdown of SWP helped me understand how my corpus will generate monthly income during retirement. Now I sleep peacefully knowing my future is secure.",
    sipAmount: 20000,
    targetCorpus: "3.2",
    retirementGoal: "Financial independence"
  },
  {
    name: "Lakshmi Devi",
    initials: "LD",
    age: 36,
    profession: "Government Employee",
    testimonial: "Even with a government pension, I wanted additional security. This retirement calculator showed me exactly how much extra I need to save for a luxurious post-retirement life.",
    sipAmount: 12000,
    targetCorpus: "2.1",
    retirementGoal: "Enhanced lifestyle in retirement"
  }
];

// Mock calculation function that mimics the real financial calculations
export const mockCalculateRetirementPlan = (formData) => {
  const {
    currentAge,
    retirementAge,
    lifeExpectancy,
    monthlyExpenditure,
    swpPercentage,
    expectedCAGR,
    inflationRate
  } = formData;

  // Convert strings to numbers
  const currentAgeNum = parseInt(currentAge);
  const retirementAgeNum = parseInt(retirementAge);
  const lifeExpectancyNum = parseInt(lifeExpectancy);
  const monthlyExpenditureNum = parseFloat(monthlyExpenditure);
  const swpPercentageNum = parseFloat(swpPercentage) / 100;
  const expectedCAGRNum = parseFloat(expectedCAGR) / 100;
  const inflationRateNum = parseFloat(inflationRate) / 100;

  // Calculate years to retirement
  const yearsToRetirement = retirementAgeNum - currentAgeNum;
  
  // Calculate retirement duration using Life Expectancy
  const retirementDuration = lifeExpectancyNum - retirementAgeNum;

  // 1. Calculate inflation-adjusted monthly expenditure at retirement
  const inflationAdjustedExpenditure = monthlyExpenditureNum * Math.pow(1 + inflationRateNum, yearsToRetirement);

  // 2. Calculate yearly expenditure at retirement
  const yearlyExpenditureAtRetirement = inflationAdjustedExpenditure * 12;

  // 3. Calculate total corpus required (yearly expenditure / SWP percentage)
  const totalCorpusRequired = yearlyExpenditureAtRetirement / swpPercentageNum;

  // 4. Calculate SIP required per month using PMT formula
  // PMT = FV * r / ((1 + r)^n - 1)
  const monthlyRate = expectedCAGRNum / 12;
  const totalMonths = yearsToRetirement * 12;
  
  let sipRequired;
  if (monthlyRate === 0) {
    sipRequired = totalCorpusRequired / totalMonths;
  } else {
    sipRequired = totalCorpusRequired * monthlyRate / (Math.pow(1 + monthlyRate, totalMonths) - 1);
  }

  // 4.1. Round SIP to whole number (as you can't invest fractional amounts)
  const sipRequiredRounded = Math.round(sipRequired);

  // 5. Calculate total corpus created through SIP using the rounded SIP amount
  let totalCorpusCreated;
  if (monthlyRate === 0) {
    totalCorpusCreated = sipRequiredRounded * totalMonths;
  } else {
    totalCorpusCreated = sipRequiredRounded * (Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate;
  }

  return {
    inflationAdjustedExpenditure: Math.round(inflationAdjustedExpenditure),
    totalCorpusRequired: Math.round(totalCorpusRequired),
    sipRequired: sipRequiredRounded,
    totalCorpusCreated: Math.round(totalCorpusCreated),
    yearsToRetirement: yearsToRetirement
  };
};